import { supabase, getCurrentUserId } from "@/lib/supabase";
import { cutoffIso, parseWhen, type PeriodKey, type ActivityItem } from "@/lib/dailyDigestPure";

export { PERIODS, cutoffIso, buildDigestPrompt } from "@/lib/dailyDigestPure";
export type { PeriodKey, ActivityItem } from "@/lib/dailyDigestPure";

/**
 * Daily Digest data layer (Batch D rewire) — reads the user's recent threads +
 * docs (RLS-scoped) for the activity journal. chat-threads is filtered in SQL
 * (real timestamps); sources/knowledgebase are fetched recent-first and
 * filtered client-side (their updatedAt columns are text/date). No backend
 * endpoint; summarization happens in the page via the host AI proxy.
 */
export async function fetchActivity(period: PeriodKey, nowMs: number): Promise<ActivityItem[]> {
  const userId = await getCurrentUserId();
  if (!userId || !supabase) return [];
  const cutoff = cutoffIso(period, nowMs);
  const cutoffMs = Date.parse(cutoff);

  const [threadsRes, sourcesRes, kbRes] = await Promise.all([
    supabase
      .from("chat-threads")
      .select("id, title, updatedAt")
      .eq("userId", userId)
      .gt("updatedAt", cutoff)
      .order("updatedAt", { ascending: false })
      .limit(60),
    supabase
      .from("sources")
      .select("id, title, updatedAt")
      .eq("userId", userId)
      .order("updatedAt", { ascending: false })
      .limit(60),
    supabase
      .from("knowledgebase")
      .select("id, title, updatedAt")
      .eq("userId", userId)
      .order("updatedAt", { ascending: false })
      .limit(60),
  ]);

  const items: ActivityItem[] = [];
  for (const r of threadsRes.data ?? []) {
    const row = r as { id: unknown; title?: string; updatedAt?: string };
    items.push({ kind: "thread", id: String(row.id), title: String(row.title ?? ""), updatedAt: String(row.updatedAt ?? "") });
  }
  for (const r of sourcesRes.data ?? []) {
    const row = r as { id: unknown; title?: string; updatedAt?: string };
    if (parseWhen(row.updatedAt) >= cutoffMs) {
      items.push({ kind: "source", id: String(row.id), title: String(row.title ?? ""), updatedAt: String(row.updatedAt ?? "") });
    }
  }
  for (const r of kbRes.data ?? []) {
    const row = r as { id: unknown; title?: string; updatedAt?: string };
    if (parseWhen(row.updatedAt) >= cutoffMs) {
      items.push({ kind: "knowledge", id: String(row.id), title: String(row.title ?? ""), updatedAt: String(row.updatedAt ?? "") });
    }
  }

  items.sort((a, b) => parseWhen(b.updatedAt) - parseWhen(a.updatedAt));
  return items;
}
